// src/views/MyMaps.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountDropdown from '../components/AccountDropdown';
import logo from '../assets/logo128x128.png';
import MapCard from '../components/MapCard';
import CreateMapModal from '../components/CreateMapModal';
import '../Homepage.css';
import { listOfflineMapIds, getOfflineMap, deleteOfflineMap } from '../utils/offlineDb';

function capitalise(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function getDisplayName(map) {
  if (
    !map.name ||
    map.name.trim() === '' ||
    map.name.trim().toLowerCase() === map.game_name?.trim().toLowerCase()
  ) {
    return { main: capitalise(map.game_name), game: null };
  }
  return { main: map.name, game: capitalise(map.game_name) };
}

function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}

function toYmdLocal(d) {
  const yy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  return `${yy}-${mm}-${dd}`;
}

function isValidYmd(s) {
  if (typeof s !== 'string') return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const t = new Date(`${s}T00:00:00.000Z`);
  if (!Number.isFinite(t.getTime())) return false;
  return t.toISOString().slice(0, 10) === s;
}

function sumViewsRows(rows) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((sum, r) => {
    const v = Number(r && r.views_billable);
    if (!Number.isFinite(v)) return sum;
    return sum + Math.trunc(v);
  }, 0);
}

function chunkArray(arr, size) {
  const out = [];
  const n = Math.max(1, Number(size) || 1);
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

function MyMapsPage({ user, setUser }) {
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [votesData, setVotesData] = useState({});
  const [votesLoading, setVotesLoading] = useState({});
  const [favLoading, setFavLoading] = useState({});
  const [favouriteMaps, setFavouriteMaps] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editMap, setEditMap] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState({});
  const [deleteError, setDeleteError] = useState(null);

  const [offlineMaps, setOfflineMaps] = useState([]);
  const [offlineModalOpen, setOfflineModalOpen] = useState(false);

  const todayLocal = useMemo(() => new Date(), []);
  const defaultTo = useMemo(() => toYmdLocal(todayLocal), [todayLocal]);
  const defaultFrom = useMemo(() => {
    const d = new Date(todayLocal.getTime());
    d.setDate(d.getDate() - 7);
    return toYmdLocal(d);
  }, [todayLocal]);

  const [viewsFrom, setViewsFrom] = useState(defaultFrom);
  const [viewsTo, setViewsTo] = useState(defaultTo);

  const [viewsLoadingRange, setViewsLoadingRange] = useState(false);
  const [viewsRangeError, setViewsRangeError] = useState(null);

  const [totalViewsAllMapsRange, setTotalViewsAllMapsRange] = useState(0);
  const [viewsByMapIdRange, setViewsByMapIdRange] = useState({});

  const navigate = useNavigate();
  const isFounder = Number(user && user.id) === 1;

  useEffect(() => {
    async function loadOffline() {
      let ids = [];
      try {
        ids = await listOfflineMapIds();
      } catch (e) {
        setOfflineMaps([]);
        return;
      }
      let bundles = [];
      try {
        bundles = await Promise.all(ids.map(id => getOfflineMap(id)));
      } catch (e) {
        setOfflineMaps([]);
        return;
      }
      setOfflineMaps(
        (bundles || [])
          .filter(Boolean)
          .sort((a, b) => (b.lastEdited || 0) - (a.lastEdited || 0))
      );
    }
    loadOffline();
  }, [offlineModalOpen]);

  useEffect(() => {
    if (!user || !user.id) {
      setMaps([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const url = isFounder ? '/api/founder/maps' : '/api/user/my-maps';

    fetch(url, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch maps');
        return res.json();
      })
      .then(mapsData => setMaps(mapsData || []))
      .catch(() => setMaps([]))
      .finally(() => setLoading(false));
  }, [user, isFounder]);

  useEffect(() => {
    if (user) {
      fetch('/api/user/favourites', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setFavouriteMaps(data))
        .catch(() => setFavouriteMaps([]));
    } else {
      setFavouriteMaps([]);
    }
  }, [user]);

  useEffect(() => {
    if (!maps.length || !user) return;
    const loadVotes = async () => {
      const newVotesData = {};
      for (let map of maps) {
        try {
          const res = await fetch(`/api/map-votes/${map.id}/${user?.id || 0}`);
          const data = await res.json();
          newVotesData[map.id] = data;
        } catch (e) {
          newVotesData[map.id] = { total_votes: map.votes || 0, user_vote: 0 };
        }
      }
      setVotesData(newVotesData);
    };
    loadVotes();
  }, [maps, user]);

  const handleVote = async (mapId, vote) => {
    if (!user) return;
    setVotesLoading(v => ({ ...v, [mapId]: true }));
    try {
      await fetch(`/api/map-votes/${mapId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: user.id, vote }),
      });
      const res = await fetch(`/api/map-votes/${mapId}/${user.id}`);
      const data = await res.json();
      setVotesData(v => ({ ...v, [mapId]: data }));
    } catch {}
    setVotesLoading(v => ({ ...v, [mapId]: false }));
  };

  const handleFavourite = async (mapId, isFavourited) => {
    if (!user) return;
    setFavLoading(l => ({ ...l, [mapId]: true }));
    try {
      await fetch(`/api/user/favourites/${mapId}`, {
        method: isFavourited ? 'DELETE' : 'POST',
        headers: { "Content-Type": "application/json" },
        credentials: 'include'
      });
      fetch('/api/user/favourites', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setFavouriteMaps(data));
    } catch {}
    setFavLoading(l => ({ ...l, [mapId]: false }));
  };

  const favMapIds = new Set(favouriteMaps.map(m => m.id));

  const filteredMaps = maps
    .filter(map => !map.parent_map_id)
    .filter(map =>
      (map.name && map.name.toLowerCase().includes(search.toLowerCase())) ||
      (map.game_name && map.game_name.toLowerCase().includes(search.toLowerCase())) ||
      (map.uploader_username && map.uploader_username.toLowerCase().includes(search.toLowerCase()))
    );

  const childrenByParentId = useMemo(() => {
    const out = {};
    for (const m of maps || []) {
      const pid = m && m.parent_map_id != null ? Number(m.parent_map_id) : null;
      const id = m && m.id != null ? Number(m.id) : null;
      if (!pid || !Number.isFinite(pid)) continue;
      if (!id || !Number.isFinite(id)) continue;
      if (!out[pid]) out[pid] = [];
      out[pid].push(id);
    }
    return out;
  }, [maps]);

  const getAggregatedViewsForMap = useMemo(() => {
    const views = viewsByMapIdRange || {};
    const children = childrenByParentId || {};

    function getSelfViews(id) {
      const v = Number(views[id] || 0);
      return Number.isFinite(v) ? Math.trunc(v) : 0;
    }

    function sumDescendants(rootId, visited) {
      if (!rootId || !Number.isFinite(rootId)) return 0;
      if (visited.has(rootId)) return 0;
      visited.add(rootId);

      const kids = children[rootId] || [];
      let sum = 0;
      for (const cid of kids) {
        sum += getSelfViews(cid);
        sum += sumDescendants(cid, visited);
      }
      return sum;
    }

    return function aggregated(id) {
      const mid = Number(id);
      if (!mid || !Number.isFinite(mid)) return 0;
      const visited = new Set();
      const total = getSelfViews(mid) + sumDescendants(mid, visited);
      return Number.isFinite(total) ? Math.trunc(total) : 0;
    };
  }, [viewsByMapIdRange, childrenByParentId]);

  useEffect(() => {
    if (!user || !user.id) return;
    if (!isValidYmd(viewsFrom) || !isValidYmd(viewsTo)) return;

    let cancelled = false;

    async function loadRangeViews() {
      setViewsLoadingRange(true);
      setViewsRangeError(null);

      try {
        let totalAll = 0;

        if (isFounder) {
          const totalRes = await fetch(
            `/api/metrics/map-views/daily?from=${encodeURIComponent(viewsFrom)}&to=${encodeURIComponent(viewsTo)}`,
            { credentials: 'include' }
          );
          if (!totalRes.ok) throw new Error('Failed to fetch total views');
          const totalJson = await totalRes.json();
          totalAll = sumViewsRows(totalJson && totalJson.rows);
        }

        const perMap = {};

        const ids = (maps || [])
          .map(m => Number(m && m.id))
          .filter(n => Number.isFinite(n) && n > 0);

        const batches = chunkArray(ids, isFounder ? 12 : 18);

        for (let bi = 0; bi < batches.length; bi++) {
          if (cancelled) return;

          const batch = batches[bi];
          const results = await Promise.all(
            batch.map(async (mapId) => {
              try {
                const r = await fetch(
                  `/api/metrics/map-views/daily?mapId=${encodeURIComponent(mapId)}&from=${encodeURIComponent(viewsFrom)}&to=${encodeURIComponent(viewsTo)}`,
                  { credentials: 'include' }
                );
                if (!r.ok) return [mapId, 0];
                const j = await r.json();
                const v = sumViewsRows(j && j.rows);
                return [mapId, Number.isFinite(v) ? v : 0];
              } catch {
                return [mapId, 0];
              }
            })
          );

          for (const [id, v] of results) perMap[id] = v;

          if (cancelled) return;
          setViewsByMapIdRange(prev => ({ ...prev, ...perMap }));
        }

        if (cancelled) return;
        setTotalViewsAllMapsRange(Number.isFinite(totalAll) ? totalAll : 0);
        setViewsByMapIdRange(perMap);
      } catch (e) {
        if (cancelled) return;
        setViewsRangeError(e && e.message ? e.message : 'Failed to load views');
        setTotalViewsAllMapsRange(0);
        setViewsByMapIdRange({});
      } finally {
        if (cancelled) return;
        setViewsLoadingRange(false);
      }
    }

    loadRangeViews();

    return () => {
      cancelled = true;
    };
  }, [user, viewsFrom, viewsTo, maps, isFounder]);

  const totalViewsDisplayedMapsRange = useMemo(() => {
    return filteredMaps.reduce((sum, m) => {
      const id = Number(m && m.id);
      if (!Number.isFinite(id) || id <= 0) return sum;
      return sum + getAggregatedViewsForMap(id);
    }, 0);
  }, [filteredMaps, getAggregatedViewsForMap]);

  function setPreset(days) {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - Math.max(0, Number(days) || 0));
    setViewsFrom(toYmdLocal(from));
    setViewsTo(toYmdLocal(to));
  }

  function setPresetThisMonth() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = now;
    setViewsFrom(toYmdLocal(from));
    setViewsTo(toYmdLocal(to));
  }

  function setPresetLastMonth() {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    setViewsFrom(toYmdLocal(from));
    setViewsTo(toYmdLocal(to));
  }

  const openEditModal = (map) => {
    if (map && map.poi_editor_access) {
      const mode = String(map.mode || "").toLowerCase();
      const basePath = mode === "vtt" || mode === "dnd" || mode === "tabletop" ? "/vtt" : "/map";
      navigate(`${basePath}/${encodeURIComponent(String(map.id))}`);
      return;
    }

    setEditMap(map);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditMap(null);
    setEditModalOpen(false);
  };

  const handleDelete = async (map) => {
    if (map && map.poi_editor_access) {
      window.alert("Only the map owner can delete this map.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${map.name || map.game_name}"?\nThis will delete all POIs for this map too!`)) {
      return;
    }
    setDeleteLoading(l => ({ ...l, [map.id]: true }));
    setDeleteError(null);
    try {
      const res = await fetch(`/api/upload-map/${map.id}`, {
        method: "DELETE",
        credentials: "include"
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to delete map.");
      setMaps(maps => maps.filter(m => m.id !== map.id));
    } catch (e) {
      setDeleteError(e.message);
    }
    setDeleteLoading(l => ({ ...l, [map.id]: false }));
  };

  const handleEditSubmit = () => {
    closeEditModal();
    window.location.reload();
  };

  const handleOpenOffline = (id) => navigate(`/offline-map/${id}`);
  const handleDeleteOffline = async (id) => {
    if (window.confirm("Delete this offline map?")) {
      await deleteOfflineMap(id);
      setOfflineMaps(offlineMaps => offlineMaps.filter(m => m.id !== id));
    }
  };

  if (loading) return <div style={{ padding: 20, color: 'white' }}>Loading...</div>;

  const fromOk = isValidYmd(viewsFrom);
  const toOk = isValidYmd(viewsTo);

  return (
    <div className="homepage">
      <header className="homepage-header" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <img
          src={logo}
          alt="Point Atlas Logo"
          style={{ width: 40, height: 40, objectFit: 'contain', cursor: 'pointer' }}
          onClick={() => navigate('/')}
          title="Go to Home"
        />
        <h1 className="brand" style={{ margin: 0 }}>Point Atlas - My Maps</h1>
        <input
          className="search-bar"
          type="text"
          placeholder="Search maps..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 200 }}
        />
        {user && <AccountDropdown user={user} />}
      </header>

      {user && (
        <>
          <h2 style={{ color: '#eee', marginTop: 32 }}>
            Online Maps{' '}
            <span style={{ fontWeight: 400, color: "#88b" }}>(Saved to Point Atlas)</span>
          </h2>

          <div
            style={{
              margin: '10px 0 18px',
              color: '#ddd',
              fontSize: 14,
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 8,
              padding: '12px 14px',
              maxWidth: 820
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Views (date range)</div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <button className="map-modal-button" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => setPreset(0)}>
                  Today
                </button>
                <button className="map-modal-button" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => setPreset(7)}>
                  Last 7 days
                </button>
                <button className="map-modal-button" style={{ padding: '6px 10px', fontSize: 12 }} onClick={() => setPreset(30)}>
                  Last 30 days
                </button>
                <button className="map-modal-button" style={{ padding: '6px 10px', fontSize: 12 }} onClick={setPresetThisMonth}>
                  This month
                </button>
                <button className="map-modal-button" style={{ padding: '6px 10px', fontSize: 12 }} onClick={setPresetLastMonth}>
                  Last month
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ color: '#bbb', fontSize: 12 }}>From</div>
                <input
                  value={viewsFrom}
                  onChange={(e) => setViewsFrom(e.target.value)}
                  placeholder="YYYY-MM-DD"
                  style={{
                    width: 120,
                    padding: '7px 8px',
                    borderRadius: 6,
                    border: fromOk ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,80,80,0.8)',
                    background: 'rgba(0,0,0,0.25)',
                    color: '#fff'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ color: '#bbb', fontSize: 12 }}>To</div>
                <input
                  value={viewsTo}
                  onChange={(e) => setViewsTo(e.target.value)}
                  placeholder="YYYY-MM-DD"
                  style={{
                    width: 120,
                    padding: '7px 8px',
                    borderRadius: 6,
                    border: toOk ? '1px solid rgba(255,255,255,0.25)' : '1px solid rgba(255,80,80,0.8)',
                    background: 'rgba(0,0,0,0.25)',
                    color: '#fff'
                  }}
                />
              </div>

              {viewsLoadingRange && (
                <div style={{ color: '#aaa', fontSize: 12 }}>Loading views…</div>
              )}

              {viewsRangeError && (
                <div style={{ color: '#ff7c7c', fontSize: 12 }}>{viewsRangeError}</div>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 12 }}>
              {isFounder && (
                <div>
                  <div style={{ color: '#bbb', fontSize: 12 }}>Total views across all maps (sitewide)</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: '#6fd3ff' }}>
                    {Number(totalViewsAllMapsRange || 0).toLocaleString()}
                  </div>
                </div>
              )}

              <div>
                <div style={{ color: '#bbb', fontSize: 12 }}>Total views across your displayed maps (parent + child)</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#b6ffb4' }}>
                  {Number(totalViewsDisplayedMapsRange || 0).toLocaleString()}
                </div>
              </div>

              <div style={{ color: '#aaa', fontSize: 12, maxWidth: 360, lineHeight: 1.35 }}>
                Note: counts are UTC-day based in the database, but the range selection uses YYYY-MM-DD strings and will match your backend’s query logic.
              </div>
            </div>
          </div>

          <div className="maps-grid">
            {filteredMaps.length === 0 && (
              <div
                style={{
                  color: 'white',
                  maxWidth: 820,
                  lineHeight: 1.45,
                  background: 'rgba(0,0,0,0.18)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 10,
                  padding: '14px 16px'
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
                  You haven&apos;t created or been given access to any online maps yet.
                </div>
              </div>
            )}

            {filteredMaps.map((map) => {
              const mapId = Number(map && map.id);
              const rangeViewsAgg = getAggregatedViewsForMap(mapId);
              const sharedAccess = !!Number(map.poi_editor_access || 0);

              return (
                <div key={map.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                  <MapCard
                    map={map}
                    user={user}
                    showVotes={true}
                    votes={votesData[map.id]?.total_votes}
                    userVote={votesData[map.id]?.user_vote}
                    onVote={(id, vote) => handleVote(id, vote)}
                    isFavourited={favMapIds.has(map.id)}
                    favLoading={favLoading[map.id]}
                    onFavourite={() => handleFavourite(map.id, favMapIds.has(map.id))}
                    showFavouriteButton={true}
                    isHidden={map.is_public === 0 || map.is_public === false}
                    showEdit={true}
                    onEdit={openEditModal}
                    showDelete={!sharedAccess}
                    onDelete={handleDelete}
                  />

                  <div
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      color: '#bfe9ff',
                      opacity: 0.95,
                      userSelect: 'none'
                    }}
                  >
                    <span aria-hidden="true">👁</span>
                    <span>{rangeViewsAgg.toLocaleString()} views in range</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ margin: '40px 0 24px', borderTop: '2px solid #444', paddingTop: 12 }}>
        <h2 style={{ color: '#eee', marginTop: 8 }}>
          Offline Maps <span style={{ fontWeight: 400, color: "#b88" }}>(Local only)</span>
        </h2>

        <button
          className="map-modal-button"
          style={{ fontSize: 15, marginBottom: 10, background: '#4bbb64', color: 'white', borderRadius: 4 }}
          onClick={() => setOfflineModalOpen(true)}
        >
          + New Offline Map
        </button>

        {offlineMaps.length === 0 && (
          <div style={{ color: '#aaa', margin: '12px 0' }}>No offline maps yet.</div>
        )}

        <div className="maps-grid">
          {offlineMaps.map(bundle => (
            <div key={bundle.id} style={{ position: 'relative' }}>
              <MapCard
                map={bundle}
                user={null}
                showVotes={false}
                showFavouriteButton={false}
                showEdit={true}
                onEdit={() => handleOpenOffline(bundle.id)}
                showDelete={true}
                onDelete={() => handleDeleteOffline(bundle.id)}
                isHidden={false}
              />
            </div>
          ))}
        </div>
      </div>

      {offlineModalOpen && (
        <CreateMapModal
          onClose={() => setOfflineModalOpen(false)}
          user={null}
          editMap={null}
          onEditSubmit={() => setOfflineModalOpen(false)}
        />
      )}

      {editModalOpen && (
        <CreateMapModal
          onClose={closeEditModal}
          user={user}
          editMap={editMap}
          onEditSubmit={handleEditSubmit}
        />
      )}

      {deleteError && (
        <div style={{ color: 'red', textAlign: 'center', marginTop: 12 }}>
          {deleteError}
        </div>
      )}
    </div>
  );
}

export default MyMapsPage;
